import React, { useEffect, useRef, useState } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { codeOrderManagement as endpoints } from '@URLS'
import { history } from './App'
import { Order } from './Order'
import { LoadingSpinner, usePageRequest } from '@CommonComponents'
import { Pagination } from '@CommonComponents/buttons'
import { OrderFilters } from './OrderFilters'

// locationKey changes on every route change, so each navigation reloads the
// orders, including a same-URL one after the filters clear, and remounts the
// filters, which closes their dropdown.
export const Orders = ({ coursenum, pageNum, locationKey }) => {
  if (pageNum === undefined || isNaN(pageNum)) {
    pageNum = 1
  }
  const [currentState, dispatch] = useStateValue()
  const [loading, setLoading] = useState(true)
  // The page lives in the URL, so only the hook's request and settle are
  // used: they hold the pager busy until the navigation's load settles.
  const { request: requestPage, settle: settlePage } = usePageRequest()
  // A failed load of a page the pager pushed puts the URL back on the last
  // page that loaded, and that navigation skips its load. A Back or Forward
  // keeps its history entry.
  const loadedUrl = useRef(null)
  const pushedUrl = useRef(null)
  const rollingBack = useRef(false)
  useEffect(() => {
    if (rollingBack.current) {
      rollingBack.current = false
      return
    }
    // A later navigation replaces this load, so its response is dropped.
    let cancelled = false
    const url = history.location.pathname + history.location.search
    const pushed = url === pushedUrl.current
    pushedUrl.current = null
    const fetchOrders = async pageNum => {
      setLoading(true)
      try {
        let resp = await client.get(
          endpoints.pageOrders({
            pageNum: pageNum,
            filterString: getQueryParams(),
          })
        )
        if (cancelled) return
        dispatch({
          type: 'updateOrderCount',
          payload: resp.data.totalOrderCount,
        })
        dispatch({
          type: 'updateOrders',
          payload: resp.data.orders,
        })
        setLoading(false)
        loadedUrl.current = url
        settlePage()
      } catch (error) {
        if (cancelled) return
        settlePage(error)
        setLoading(false)
        if (pushed && loadedUrl.current !== null) {
          rollingBack.current = true
          history.replace(loadedUrl.current)
        }
        console.error(error)
      }
    }
    fetchOrders(pageNum)
    return () => {
      cancelled = true
    }
  }, [pageNum, locationKey])

  const getQueryParams = () => {
    let params = new URLSearchParams()
    let filterKeys = Object.keys(currentState.filters)
    for (let i = 0; i < filterKeys.length; i++) {
      let key = filterKeys[i]
      if (currentState.filters[key] !== '') {
        params.append(key, currentState.filters[key])
      }
    }
    return params.toString()
  }

  const updatePageNum = ({ pageNum, queryParams }) => {
    history.push(
      `/account/admin_coupons.management/${coursenum}/?page_num=${pageNum}${
        queryParams ? '&' + queryParams : ''
      }`
    )
  }

  const changePage = page => {
    updatePageNum({ pageNum: page })
    pushedUrl.current = history.location.pathname + history.location.search
    return requestPage(page)
  }

  // resultsPerPage and pageNum arrive as strings.
  const rate = Number(currentState.resultsPerPage)
  const pages = Math.ceil(currentState.totalOrderCount / rate)

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between mb-3">
        <h2 className="mb-2 mt-0 w-50">Orders</h2>
        <div className="mt-3 pointer w-50 text-right">
          <OrderFilters
            key={locationKey}
            pageNum={pageNum}
            coursenum={coursenum}
          />
        </div>
      </div>
      {loading ? (
        <div className="text-center">
          <LoadingSpinner style={{ fontSize: '50px' }} />
        </div>
      ) : (
        <div>
          {currentState.orders.length > 0 ? (
            currentState.orders.map((order, i) => (
              <Order
                key={i}
                orderId={order.id}
                orderedCourse={order.coursenum}
                orderedCourseTitle={order.courseTitle}
                created={order.created}
                numCodes={order.numCodes}
                contactName={order.contactName}
                contactEmail={order.contactEmail}
                institution={order.institution}
                invoiceStatus={order.invoiceStatus}
                index={i}
                invoiceNum={order.invoiceNum}
                pmtDate={order.pmtDate}
                sentOn={order.sentOn}
              />
            ))
          ) : (
            <div>
              <h4>No access code orders exist</h4>
            </div>
          )}
        </div>
      )}
      {pages > 1 && (
        <Pagination
          currentPage={Number(pageNum)}
          rate={rate}
          total={currentState.totalOrderCount}
          setCurrentPage={changePage}
        />
      )}
    </React.Fragment>
  )
}
